import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';
import type {
  AddSectionRequest,
  CreateStoryConceptRequest,
  GenerateOutlineRequest,
  ReplaceOutlineRequest,
  StoryConcept,
  StoryConceptDetail,
  StoryOutlineSection,
  UpdateSectionRequest,
  UpdateStoryConceptRequest,
} from '../domain';

const BASE = '/api/v3/storyteller';

/**
 * Storyteller sub-surface API. Concept CRUD + outline generation/replace +
 * section CRUD. The Ok/Created endpoints return the ApiResponse envelope
 * (unwrapped via callV3); the two DELETEs return 204 No Content, so they use a
 * raw axios call (callV3 would choke on an empty body).
 */
export const StorytellerService = {
  listConcepts: () =>
    callV3<StoryConcept[]>(api.get<ApiResponse<StoryConcept[]>>(`${BASE}/concepts`)),

  createConcept: (req: CreateStoryConceptRequest) =>
    callV3<StoryConcept>(api.post<ApiResponse<StoryConcept>>(`${BASE}/concepts`, req)),

  getConcept: (conceptId: string) =>
    callV3<StoryConceptDetail>(
      api.get<ApiResponse<StoryConceptDetail>>(`${BASE}/concepts/${conceptId}`),
    ),

  updateConcept: (conceptId: string, req: UpdateStoryConceptRequest) =>
    callV3<StoryConceptDetail>(
      api.patch<ApiResponse<StoryConceptDetail>>(`${BASE}/concepts/${conceptId}`, req),
    ),

  deleteConcept: async (conceptId: string): Promise<void> => {
    await api.delete(`${BASE}/concepts/${conceptId}`);
  },

  generateOutline: (conceptId: string, req: GenerateOutlineRequest = {}) =>
    callV3<StoryConceptDetail>(
      api.post<ApiResponse<StoryConceptDetail>>(`${BASE}/concepts/${conceptId}/outline`, req),
    ),

  replaceOutline: (conceptId: string, req: ReplaceOutlineRequest) =>
    callV3<StoryConceptDetail>(
      api.put<ApiResponse<StoryConceptDetail>>(`${BASE}/concepts/${conceptId}/outline`, req),
    ),

  addSection: (conceptId: string, req: AddSectionRequest) =>
    callV3<StoryOutlineSection>(
      api.post<ApiResponse<StoryOutlineSection>>(`${BASE}/concepts/${conceptId}/sections`, req),
    ),

  updateSection: (sectionId: string, req: UpdateSectionRequest) =>
    callV3<StoryOutlineSection>(
      api.patch<ApiResponse<StoryOutlineSection>>(`${BASE}/sections/${sectionId}`, req),
    ),

  deleteSection: async (sectionId: string): Promise<void> => {
    await api.delete(`${BASE}/sections/${sectionId}`);
  },
};
